import * as React from "react";
import { Input, Spinner } from "react-ui";
import { useCallback, useMemo } from "react";
import debounce from "lodash.debounce";

import { Icon } from "../../../Icons/Icon";
import { MetricsActionPayload, trackAction } from "../../../Internals/Metrics/MetricsProvider";
import { FILTER_LIST_CHECKED, FILTER_LIST_SEARCH } from "../../../metricsAction";

import { FilterItem } from "./FilterItem/FilterItem";
import * as styles from "./FilterList.styles";

export interface Item {
    id: string;
    description?: string;
    values: string[];
}

export interface FilterListProps {
    items: Item[];
    checkedItems: Item[];
    onSelectItem: (itemId: string, checked: boolean) => void;
    onSearchChange: (value: string) => void;
    onRemoveFilter: () => void;
    inputPlaceholder?: string;
    separator: string;
    notFoundText: string;
    searchingForItemsCount: number;
    loading: boolean;
    searching: boolean;
    makeBaseValuesForMetrics: () => {};
}

function checkedSelector(makeBaseValuesForMetrics: () => {}, values: string[], checked: boolean): MetricsActionPayload {
    return { ...makeBaseValuesForMetrics(), value: values.join("-"), checked: checked };
}

function searchSelector(makeBaseValuesForMetrics: () => {}, value: string): MetricsActionPayload {
    return { ...makeBaseValuesForMetrics(), value: value };
}

const renderLoader = (isLoading: boolean): JSX.Element | undefined =>
    isLoading ? (
        <div css={styles.loader}>
            <Spinner />
        </div>
    ) : undefined;

const renderSearchInput = (
    _displayedItems: Item[],
    inputPlaceholder: string | undefined,
    handleSearch: (searchString: string) => void
): JSX.Element | undefined => (
    <div css={styles.input}>
        <Input
            data-tid="FilterSearchInput"
            leftIcon={<Icon name="SearchLoupeIcon" />}
            placeholder={inputPlaceholder}
            width="100%"
            onValueChange={handleSearch}
        />
    </div>
);

export const FilterList = (props: FilterListProps) => {
    const handleOnCLick = useCallback(
        (item: Item, checked: boolean) => {
            trackAction(FILTER_LIST_CHECKED, () =>
                checkedSelector(props.makeBaseValuesForMetrics, item.values, checked)
            );
            props.onSelectItem(item.id, checked);
        },
        [props.onSelectItem]
    );

    const handleOnSearchChangeInput = useMemo(
        () =>
            debounce((searchString: string) => {
                trackAction(FILTER_LIST_SEARCH, () => searchSelector(props.makeBaseValuesForMetrics, searchString));
                props.onSearchChange(searchString);
            }, 700),
        [props.onSearchChange]
    );

    const displayedItems: Item[] = props.items;

    const checkedItems = props.checkedItems.map((item, index) => (
        <FilterItem
            description={item.description}
            checked={true}
            key={`${item.values.join("-")}-${index}`}
            values={item.values}
            onClick={(_, checked) => handleOnCLick(item, checked)}
            separator={props.separator}
            singleElement={props.checkedItems.length === 1}
        />
    ));

    const filterItems = displayedItems.map((item, index) => (
        <FilterItem
            description={item.description}
            checked={false}
            key={`${item.values.join("-")}-${index}`}
            values={item.values}
            onClick={(_, checked) => handleOnCLick(item, checked)}
            separator={props.separator}
            singleElement={displayedItems.length === 1}
        />
    ));

    return (
        <div css={styles.container}>
            {displayedItems.length + checkedItems.length > props.searchingForItemsCount || props.searching
                ? renderSearchInput(displayedItems, props.inputPlaceholder, handleOnSearchChangeInput)
                : undefined}
            {props.loading ? (
                renderLoader(props.loading)
            ) : (
                <>
                    {checkedItems}
                    {checkedItems.length > 0 ? <div css={styles.delimiter} /> : undefined}
                    {displayedItems.length === 0 ? (
                        <div css={styles.text}>{props.searching ? props.notFoundText : "Фильтровать нечего"}</div>
                    ) : (
                        filterItems
                    )}
                </>
            )}
        </div>
    );
};
